import { useCallback, useEffect, useState } from 'react';

import type { KeyedMutator, SWRConfiguration } from 'swr';
import useSWR from 'swr';

//type
import type { CompetitorType } from '@/hooks/swr/useCompetitors';

// Utils
import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import type { InstagramMediaType } from '@/types/utils/mediaType';

// API Endpoints
import { COMPETITOR_ANALYTICS, COMPETITORS_ANALYTICS } from '@/routes/apiEndpoints';

type GraphDataType = {
    data: string;
    value: number;
    avg?: number;
};

export type MutateCompetitorsAnalyticsType = KeyedMutator<CompetitorsAnalyticsType>;

type CompetitorsAnalyticsType = {
    analytics: Record<
        string,
        | {
              data_complete: boolean;
              value?: number;
              type_split?: Record<InstagramMediaType, number>;
              followers_estimated?: number;
          }
        | { graphData?: GraphDataType[] }
    >;
    competitors: CompetitorType[];
};

function useCompetitorsAnalytics<TParams extends { [key: string]: { type: string } }>(
    SPID: number,
    competitorID: CompetitorType['id'],
    params: { parameters?: TParams } = {},
    options: SWRConfiguration<CompetitorsAnalyticsType, Error> = {
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    }
) {
    const [state, setState] = useState<CompetitorsAnalyticsType['analytics']>(null);
    const [requestParams, setRequestParams] = useState<{ parameters?: TParams }>(params);

    const getUrl = useCallback(() => {
        if (competitorID) {
            return [
                injectEndpointUrlParams(COMPETITOR_ANALYTICS, {
                    competitorID,
                    SPID
                }),
                ...Object.values(requestParams)
            ];
        }
        return [
            injectEndpointUrlParams(COMPETITORS_ANALYTICS, {
                SPID
            }),
            ...Object.values(requestParams)
        ];
    }, [competitorID, SPID, requestParams]);

    const { data, mutate, error, isLoading } = useSWR<CompetitorsAnalyticsType, Error>(
        getUrl(),
        ([url]) => get(url, requestParams).then((res) => res.data),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    useEffect(() => {
        setRequestParams(params);
    }, [params]);

    useEffect(() => {
        if (!data?.analytics && !error) {
            return;
        }
        setState(data?.analytics);
    }, [error, data?.analytics]);

    return {
        analytics: state || {},
        competitors: data?.competitors || [],
        mutate,
        setRequestParams,
        setState,
        error,
        isLoading
    };
}

export default useCompetitorsAnalytics;
